import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";
import RelationshipVisualizer from "@/components/agents/RelationshipVisualizer";
import {
  Brain, Shield, Search, FileText, Download, Loader2,
  Clock, CheckCircle, XCircle, AlertTriangle, User, Building2,
  Heart, ExternalLink, History, Settings, Activity, Share2,
} from "lucide-react";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SCOPES = [
  { value: "", label: "General Due Diligence" },
  { value: "pre-employment_vetting", label: "Pre-Employment Vetting" },
  { value: "partnership_due_diligence", label: "Partnership Due Diligence" },
  { value: "security_clearance", label: "Security Clearance" },
  { value: "client_onboarding", label: "Client Onboarding" },
  { value: "investment_screening", label: "Investment Screening" },
  { value: "media_background_check", label: "Media Background Check" },
];

const PROGRESS_STEPS = [
  { key: "queued", label: "Queued", icon: Clock },
  { key: "resolving", label: "Resolving entity identity...", icon: Search },
  { key: "gathering", label: "Querying public records, social media & news...", icon: Shield },
  { key: "analyzing", label: "Analyzing with AI...", icon: Brain },
  { key: "generating_report", label: "Generating report...", icon: FileText },
  { key: "completed", label: "Report ready!", icon: CheckCircle },
];

export default function AgentsCentre() {
  const [activeTab, setActiveTab] = useState("profiler");
  const [entityName, setEntityName] = useState("");
  const [entityType, setEntityType] = useState("");
  const [details, setDetails] = useState("");
  const [scope, setScope] = useState("");
  const [investigating, setInvestigating] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [investigationResult, setInvestigationResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [breachData, setBreachData] = useState(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // Fetch history on mount
  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/agent-profiler/history`, { headers: getHeaders(), withCredentials: true });
      setHistory(res.data || []);
    } catch (_) {}
    setLoadingHistory(false);
  };

  const startInvestigation = async () => {
    if (!entityName.trim()) {
      toast.error("Please enter an entity name");
      return;
    }

    setInvestigating(true);
    setProgressStep(0);
    setError(null);
    setInvestigationResult(null);

    // Animate progress steps
    const progressInterval = setInterval(() => {
      setProgressStep((prev) => Math.min(prev + 1, 4));
    }, 8000);

    try {
      const res = await axios.post(`${API}/agent-profiler/investigate`,
        {
          entity_name: entityName.trim(),
          entity_type: entityType || undefined,
          details: details.trim() || undefined,
          scope: scope || undefined,
        },
        { headers: getHeaders(), withCredentials: true }
      );

      clearInterval(progressInterval);
      setProgressStep(5); // completed
      setInvestigationResult(res.data);
      setSelectedReportId(res.data.id);
      toast.success("Investigation complete!");

      // Fetch graph and breach data in background
      if (res.data.has_graph) {
        setLoadingGraph(true);
        axios.get(`${API}/agent-profiler/investigation/${res.data.id}/graph`, {
          headers: getHeaders(), withCredentials: true,
        }).then((g) => setGraphData(g.data)).catch(() => {}).finally(() => setLoadingGraph(false));
      }
      if (res.data.has_dehashed) {
        axios.get(`${API}/agent-profiler/investigation/${res.data.id}/breach-data`, {
          headers: getHeaders(), withCredentials: true,
        }).then((b) => setBreachData(b.data)).catch(() => {});
      }

      fetchHistory();
    } catch (err) {
      clearInterval(progressInterval);
      setError(err.response?.data?.detail || err.message || "Investigation failed");
      setProgressStep(0);
    }
    setInvestigating(false);
  };

  const downloadReport = async (invId) => {
    try {
      const res = await axios.get(`${API}/agent-profiler/report/${invId}/download`, {
        headers: getHeaders(),
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Entity_Profile_Report_${invId.slice(0, 8)}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch (err) {
      toast.error("Failed to download report");
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      completed: "bg-emerald-600",
      failed: "bg-red-600",
      resolving: "bg-amber-500",
      gathering: "bg-amber-500",
      analyzing: "bg-blue-500",
      generating_report: "bg-blue-500",
    };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 text-white rounded ${colors[status] || "bg-slate-500"}`}>
        {status?.replace(/_/g, " ")}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Brain className="w-5 h-5 text-violet-400" /> Agents Centre
            </h1>
            <p className="text-xs text-zinc-500">AI-powered intelligence and profiling tools</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="bg-slate-800 border border-slate-700 rounded-lg p-1">
            <TabsTrigger value="profiler" className="rounded-md data-[state=active]:bg-violet-600 data-[state=active]:text-white whitespace-nowrap">
              <Brain className="w-4 h-4 mr-2" />
              Agent Profiler
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-md data-[state=active]:bg-violet-600 data-[state=active]:text-white whitespace-nowrap">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            <TabsTrigger value="graph" className="rounded-md data-[state=active]:bg-violet-600 data-[state=active]:text-white whitespace-nowrap">
              <Share2 className="w-4 h-4 mr-2" />
              Relationship Graph
            </TabsTrigger>
          </TabsList>

          {/* Profiler Tab */}
          <TabsContent value="profiler" className="mt-4 space-y-4">
            {/* Info Card */}
            <Card className="bg-slate-800/80 border border-slate-700 p-4">
              <div className="flex items-start gap-3">
                <Brain className="w-8 h-8 text-violet-400 shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-white">Agent Profiler</h3>
                  <p className="text-xs text-slate-400 mt-1">
                    Conducts comprehensive due diligence on any person, business, or NGO.
                    Scans public records, social media, news (in 33 languages), and authorised
                    intelligence sources. Generates a detailed, structured Entity Profile Report
                    in Word format for informed decision-making.
                  </p>
                </div>
              </div>
            </Card>

            {/* Search Form */}
            <Card className="bg-slate-800/80 border border-slate-700">
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Entity Name *</Label>
                    <Input
                      value={entityName}
                      onChange={(e) => setEntityName(e.target.value)}
                      placeholder="e.g. John Smith, XYZ Corporation, Hope Foundation"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">Entity Type</Label>
                    <Select value={entityType} onValueChange={setEntityType}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Auto-detect" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        <SelectItem value="">Auto-detect</SelectItem>
                        <SelectItem value="Natural Person">Natural Person</SelectItem>
                        <SelectItem value="Business Entity">Business Entity</SelectItem>
                        <SelectItem value="Non-Governmental Organisation">NGO / Non-Profit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-400 mb-1 block">Additional Details (optional)</Label>
                  <Textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Location, known associates, registration number, or any other context..."
                    className="bg-slate-700 border-slate-600 text-white min-h-[60px]"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-48">
                    <Label className="text-xs text-slate-400 mb-1 block">Investigation Scope</Label>
                    <Select value={scope} onValueChange={setScope}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="General Due Diligence" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 text-white">
                        {SCOPES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={startInvestigation}
                    disabled={investigating || !entityName.trim()}
                    className="bg-violet-600 hover:bg-violet-500 disabled:bg-slate-600 text-white mt-5"
                  >
                    {investigating ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Investigating...</>
                    ) : (
                      <><Search className="w-4 h-4 mr-2" /> Investigate</>
                    )}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Progress */}
            {investigating && (
              <Card className="bg-slate-800/80 border border-violet-600/40">
                <div className="p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-violet-400">Investigation in Progress</h4>
                  {PROGRESS_STEPS.slice(0, 5).map((step, i) => {
                    const Icon = step.icon;
                    const active = i === progressStep;
                    const done = i < progressStep;
                    return (
                      <div key={step.key} className={`flex items-center gap-2 text-xs ${done ? "text-emerald-400" : active ? "text-white" : "text-slate-600"}`}>
                        {done ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : active ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />
                        )}
                        <span>{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Error */}
            {error && (
              <Card className="bg-red-900/30 border border-red-700/50 p-4">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <p className="text-sm text-red-300">{error}</p>
                </div>
              </Card>
            )}

            {/* Results */}
            {investigationResult && (
              <Card className="bg-slate-800/80 border border-emerald-600/40">
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <h4 className="text-sm font-semibold text-emerald-400">Investigation Complete</h4>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-900/50 rounded p-2">
                      <p className="text-[10px] text-slate-400">Report Ref</p>
                      <p className="text-xs font-mono text-white">{investigationResult.report_reference}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded p-2">
                      <p className="text-[10px] text-slate-400">Entity Type</p>
                      <p className="text-xs text-white">{investigationResult.entity_type}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded p-2">
                      <p className="text-[10px] text-slate-400">Status</p>
                      <p className="text-xs text-emerald-400">{investigationResult.status}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded p-2">
                      <p className="text-[10px] text-slate-400">Breach Findings</p>
                      <p className="text-xs text-white">
                        {investigationResult.has_dehashed
                          ? `${investigationResult.total_breach_findings} entries`
                          : "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* DeHashed Breach Summary */}
                  {breachData && breachData.total_findings > 0 && (
                    <div className="bg-slate-900/50 rounded p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                        <h5 className="text-xs font-semibold text-amber-400">Breach Intelligence</h5>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-slate-400">Total Entries:</span> <span className="text-white font-mono">{breachData.total_findings}</span></div>
                        <div><span className="text-slate-400">Searches:</span> <span className="text-white font-mono">{breachData.searches?.length || 0}</span></div>
                        <div><span className="text-slate-400">Emails:</span> <span className="text-white font-mono">{(breachData.aggregated_findings?.emails?.size) || breachData.aggregated_findings?.emails?.length || 0}</span></div>
                        <div><span className="text-slate-400">Usernames:</span> <span className="text-white font-mono">{(breachData.aggregated_findings?.usernames?.size) || breachData.aggregated_findings?.usernames?.length || 0}</span></div>
                      </div>
                      {/* Risk badges */}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {breachData.risk_indicators?.map((ind, i) => (
                          <span key={i}
                            className={`text-[10px] px-1.5 py-0.5 rounded ${
                              ind.severity === "CRITICAL" ? "bg-red-900/50 text-red-300" :
                              ind.severity === "HIGH" ? "bg-orange-900/50 text-orange-300" :
                              ind.severity === "MEDIUM" ? "bg-yellow-900/50 text-yellow-300" :
                              "bg-slate-700 text-slate-300"
                            }`}
                          >
                            [{ind.severity}] {ind.type.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      size="sm"
                      onClick={() => downloadReport(investigationResult.id)}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                    >
                      <Download className="w-4 h-4 mr-1" /> Download Word Report
                    </Button>
                    {graphData && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setActiveTab("graph")}
                        className="text-xs border-slate-600 text-slate-300 hover:text-white"
                      >
                        <Share2 className="w-4 h-4 mr-1" /> View Relationship Graph
                      </Button>
                    )}
                    <Link
                      to={`/agents-centre?report=${investigationResult.id}`}
                      className="text-xs text-slate-400 hover:text-white underline"
                    >
                      View in History
                    </Link>
                  </div>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4 space-y-4">
            <Card className="bg-slate-800/80 border border-slate-700">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <History className="w-4 h-4 text-violet-400" /> Investigation History
                </h3>
              </div>
              <div className="p-4">
                {loadingHistory ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No investigations yet</p>
                    <p className="text-xs text-slate-500 mt-1">Run your first investigation above</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {history.map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between bg-slate-900/50 border border-slate-700/50 px-3 py-2 rounded">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white truncate">
                              {inv.entity_name}
                            </span>
                            <StatusBadge status={inv.status} />
                            {inv.report_reference && (
                              <span className="text-[10px] text-slate-500 font-mono">{inv.report_reference}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            {inv.entity_type && (
                              <span className="text-[10px] text-slate-400">{inv.entity_type}</span>
                            )}
                            {inv.created_at && (
                              <span className="text-[10px] text-slate-500">{new Date(inv.created_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0 ml-2">
                          {inv.status === "completed" && (
                            <button
                              onClick={() => downloadReport(inv.id)}
                              className="text-[10px] bg-emerald-700 hover:bg-emerald-600 text-white px-2 py-1 rounded flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {/* Relationship Graph Tab */}
          <TabsContent value="graph" className="mt-4">
            <Card className="bg-slate-800/80 border border-slate-700">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-violet-400" /> Relationship Network
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Visual graph of entity connections from DeHashed breach intelligence, social media, and web data.
                </p>
              </div>
              <div className="p-4">
                {!selectedReportId ? (
                  <div className="text-center py-8">
                    <Share2 className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No investigation results yet</p>
                    <p className="text-xs text-slate-500 mt-1">Run an investigation first to see its relationship graph</p>
                  </div>
                ) : loadingGraph ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : graphData ? (
                  <RelationshipVisualizer graphData={graphData} loading={false} />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-400">No relationship data available for this investigation</p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
